import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { useCampaigns } from '../lib/campaign.tsx'
import { ROLE_LABELS } from '../lib/roles.ts'

/**
 * People: who has an account, what they hold, and the three things an
 * administrator does to them (§6a, §6c).
 *
 * Every list and every action is filtered by the database, not by this screen —
 * `auth.admin_list_users` returns only the people the caller may administer,
 * and each action refuses independently. What this file decides is what to
 * OFFER. A hidden button is a courtesy; the control is one layer down.
 *
 * No email is sent anywhere. An invitation and a reset each produce a
 * single-use link shown exactly once, for the administrator to deliver out of
 * band — which §6c already requires for identity verification on a reset, and
 * which is the only option until transactional email exists.
 */

interface Grant {
  grant_id: string
  role: string
  scope_type: string
  scope_id: string | null
  scope_name: string | null
}

interface AdminUser {
  user_id: string
  email: string
  display_name: string | null
  status: string
  is_protected: boolean
  last_login_at: string | null
  confirmed_factors: number
  grants: Grant[]
}

interface Invitation {
  invitation_id: string
  email: string
  role: string
  scope_type: string
  scope_name: string | null
  invited_by_email: string | null
  created_at: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
}

/** The link an administrator hands over. Built here because only the browser knows the origin. */
function linkFor(path: string, token: string): string {
  return `${window.location.origin}${path}?token=${encodeURIComponent(token)}`
}

export function Admin() {
  const client = useQueryClient()
  const { data: campaignData } = useCampaigns()
  const campaigns = campaignData?.campaigns ?? []

  const [issued, setIssued] = useState<{ label: string; url: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiGet<{ users: AdminUser[] }>('/admin/users'),
  })

  const invitations = useQuery({
    queryKey: ['admin-invitations'],
    queryFn: () => apiGet<{ invitations: Invitation[] }>('/admin/invitations'),
  })

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('campaign_manager')
  const [inviteScope, setInviteScope] = useState('')

  function report(caught: unknown) {
    setError(
      caught instanceof ApiError || caught instanceof Error
        ? caught.message
        : 'That did not work.',
    )
  }

  const invite = useMutation({
    mutationFn: async () => {
      // A platform role has no scope id; a campaign role must have one. The
      // database enforces both — this keeps an obviously malformed request from
      // being made at all.
      const platform = inviteRole === 'super_admin' || inviteRole === 'org_admin'
      return apiPost<{ token: string }>('/admin/invitations', {
        email: inviteEmail.trim(),
        role: inviteRole,
        scope_type: platform ? 'platform' : 'campaign',
        scope_id: platform ? null : inviteScope || null,
      })
    },
    onSuccess: async (result) => {
      setError(null)
      setIssued({ label: `Invitation for ${inviteEmail.trim()}`, url: linkFor('/accept', result.token) })
      setInviteEmail('')
      await client.invalidateQueries({ queryKey: ['admin-invitations'] })
    },
    onError: report,
  })

  const resetPassword = useMutation({
    mutationFn: (user: AdminUser) =>
      apiPost<{ token: string }>(`/admin/users/${user.user_id}/password-reset`).then((r) => ({
        r,
        user,
      })),
    onSuccess: ({ r, user }) => {
      setError(null)
      setIssued({ label: `Password reset for ${user.email}`, url: linkFor('/reset', r.token) })
    },
    onError: report,
  })

  const resetMfa = useMutation({
    mutationFn: (user: AdminUser) =>
      apiPost<{ factors_removed: number }>(`/admin/users/${user.user_id}/mfa-reset`),
    onSuccess: async () => {
      setError(null)
      setIssued(null)
      await client.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: report,
  })

  const revokeInvite = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/invitations/${id}/revoke`),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['admin-invitations'] })
    },
    onError: report,
  })

  const revokeGrant = useMutation({
    mutationFn: (id: string) => apiPost(`/admin/grants/${id}/revoke`),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: report,
  })

  const pending = (invitations.data?.invitations ?? []).filter(
    (i) => !i.accepted_at && !i.revoked_at && new Date(i.expires_at) > new Date(),
  )

  const campaignRole = inviteRole !== 'super_admin' && inviteRole !== 'org_admin'

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">People</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Everyone on the platform and what they hold, looked up by person. Assigning people to a
        campaign is done from that campaign's Team tab.
      </p>

      {error ? (
        <p
          role="alert"
          className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink"
        >
          {error}
        </p>
      ) : null}

      {/* Shown once, and never recoverable: what is stored is the SHA-256 of
          the token. Reissuing is the only way to get another. */}
      {issued ? (
        <section className="mt-4 rounded-lg border border-gold-500 bg-surface-sunken p-4">
          <h2 className="text-sm font-semibold">{issued.label}</h2>
          <p className="mt-1 text-xs text-ink-muted">
            Copy this now — it is shown once and cannot be recovered. Send it by a channel you
            trust, and confirm who you are speaking to before you do.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto rounded border border-line bg-surface px-2 py-1.5 text-xs">
              {issued.url}
            </code>
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(issued.url)}
              className="rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold hover:border-brand-400"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setIssued(null)}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Dismiss
            </button>
          </div>
        </section>
      ) : null}

      <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
        <h2 className="text-sm font-semibold">Invite someone</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
              Email
            </span>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-64 rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
              Role
            </span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
            >
              {Object.entries(ROLE_LABELS)
                .filter(([value]) => value !== 'canvasser')
                .map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          {campaignRole ? (
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                Campaign
              </span>
              <select
                value={inviteScope}
                onChange={(e) => setInviteScope(e.target.value)}
                className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
              >
                <option value="">Choose a campaign</option>
                {campaigns.map((c) => (
                  <option key={c.campaign_id} value={c.campaign_id}>
                    {c.org_name} — {c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <button
            type="button"
            disabled={!inviteEmail.trim() || (campaignRole && !inviteScope) || invite.isPending}
            onClick={() => invite.mutate()}
            className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {invite.isPending ? 'Creating…' : 'Create invitation'}
          </button>
        </div>
        <p className="mt-3 text-xs text-ink-muted">
          Usually easier from a campaign's Team tab. Someone who already has an account signs in
          and accepts — their password is never touched by an invitation.
        </p>
      </section>

      <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">Accounts</h2>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[48rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">Person</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Roles</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Status</th>
              <th scope="col" className="py-2 pr-4 font-semibold">2FA</th>
              <th scope="col" className="relative py-2 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {users.isPending ? (
              <tr><td colSpan={5} className="py-4 text-ink-muted">Loading…</td></tr>
            ) : (users.data?.users ?? []).length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-ink-muted">
                There is nobody you may administer.
              </td></tr>
            ) : (
              (users.data?.users ?? []).map((u) => (
                <tr key={u.user_id} className="border-b border-line/60 align-top">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{u.display_name ?? u.email}</div>
                    <div className="text-xs text-ink-muted">{u.email}</div>
                  </td>
                  <td className="py-2 pr-4">
                    {u.grants.length === 0 ? (
                      <span className="text-ink-muted">none</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {u.grants.map((g) => (
                          <li key={g.grant_id} className="flex items-center gap-2">
                            <span>
                              {ROLE_LABELS[g.role] ?? g.role}
                              {g.scope_name ? (
                                <span className="text-ink-muted"> · {g.scope_name}</span>
                              ) : null}
                            </span>
                            {u.is_protected ? null : (
                              <button
                                type="button"
                                onClick={() => revokeGrant.mutate(g.grant_id)}
                                className="text-xs text-ink-muted hover:text-ink"
                                title="Revoke this grant"
                              >
                                revoke
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {u.status}
                    {u.is_protected ? (
                      <div className="text-xs text-ink-muted">protected</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4 tabular-nums">
                    {u.confirmed_factors === 0 ? (
                      // Not a warning: an account with no factor cannot log in
                      // at all, so this reads as "waiting to enrol".
                      <span className="text-ink-muted">not enrolled</span>
                    ) : (
                      `${u.confirmed_factors} factor${u.confirmed_factors === 1 ? '' : 's'}`
                    )}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => resetPassword.mutate(u)}
                      className="mr-3 text-xs font-semibold text-brand-700 hover:underline"
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          confirm(
                            `Remove every second factor for ${u.email}? They will be signed out ` +
                              `everywhere and must enrol again before they can log in.\n\n` +
                              `Confirm who you are speaking to first — this is the step an ` +
                              `attacker would ask you to take.`,
                          )
                        ) {
                          resetMfa.mutate(u)
                        }
                      }}
                      className="text-xs font-semibold text-brand-700 hover:underline"
                    >
                      Reset 2FA
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
        Pending invitations
      </h2>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">Email</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Role</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Invited by</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Expires</th>
              <th scope="col" className="relative py-2 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-ink-muted">No invitations outstanding.</td></tr>
            ) : (
              pending.map((i) => (
                <tr key={i.invitation_id} className="border-b border-line/60">
                  <td className="py-2 pr-4">{i.email}</td>
                  <td className="py-2 pr-4">
                    {ROLE_LABELS[i.role] ?? i.role}
                    {i.scope_name ? <span className="text-ink-muted"> · {i.scope_name}</span> : null}
                  </td>
                  <td className="py-2 pr-4 text-ink-muted">{i.invited_by_email ?? '—'}</td>
                  <td className="py-2 pr-4 text-ink-muted">{i.expires_at.slice(0, 10)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => revokeInvite.mutate(i.invitation_id)}
                      className="text-xs text-ink-muted hover:text-ink"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  )
}
